import React, { useState, useEffect, useCallback } from "react";
import { IconButton, Badge, Popover } from "@mui/material";
import NotificationsIcon from "@mui/icons-material/Notifications";
import { useAuth } from "../../contexts/AuthContext";
import {
  getBidAlertShowFlag,
  getBidShowAlertList,
  unSetBadgeNums,
} from "../../utils/api";
import { useNavigate } from "react-router-dom";
import { formatMeasure, formatMoney } from "../../utils/formatNumber";

const joinLocation = (...parts) =>
  parts
    .map((part) => (part == null ? "" : String(part).trim()))
    .filter(Boolean)
    .join(", ");

const formatDeadhead = (value) =>
  formatMeasure(value, "mi", { fallback: "" });

const withDeadhead = (location, deadhead) => {
  if (!location) return "—";
  const dh = formatDeadhead(deadhead);
  return dh ? `${location} (${dh})` : location;
};

const getAlertRoute = (alert) => {
  const load = alert?.content?.loadData;
  const data = load?.data || load || {};
  const detail = data?.detailPayload || {};
  const bid = alert?.content?.bidData || {};

  let origin = "";
  let destination = "";
  let originDh = data?.dh_o ?? data?.originDeadhead ?? detail?.dh_o;
  let destinationDh = data?.dh_d ?? data?.destinationDeadhead ?? detail?.dh_d;

  if (detail?.orgCity || detail?.desCity) {
    origin = joinLocation(detail.orgCity, detail.orgState);
    destination = joinLocation(detail.desCity, detail.desState);
  } else if (data?.originCity || data?.destinationCity) {
    origin = joinLocation(data.originCity, data.originState);
    destination = joinLocation(data.destinationCity, data.destinationState);
  } else if (typeof data?.origin === "string" || typeof data?.destination === "string") {
    origin = data.origin || "";
    destination = data.destination || "";
  } else if (data?.origin?.city || data?.destination?.city) {
    origin = joinLocation(data.origin?.city, data.origin?.state);
    destination = joinLocation(
      data.destination?.city,
      data.destination?.state
    );
  } else if (Array.isArray(data?.stops) && data.stops.length > 0) {
    const first = data.stops[0];
    const last = data.stops[data.stops.length - 1];
    origin =
      first?.location ||
      joinLocation(first?.city, first?.state || first?.stateCode);
    destination =
      last?.location ||
      joinLocation(last?.city, last?.state || last?.stateCode);
  }

  const amount = bid?.amount ?? data?.amount;
  const platform =
    bid?.platform ||
    alert?.platform ||
    data?.platform ||
    load?.platform ||
    "";

  return {
    origin: withDeadhead(origin, originDh),
    destination: withDeadhead(destination, destinationDh),
    amount: formatMoney(amount, { fallback: null }),
    platform: platform ? String(platform) : null,
  };
};

const PopupAlert = () => {
  const [anchorEl, setAnchorEl] = useState(null);
  const { user, socket } = useAuth();
  const [alertNum, setAlertNum] = useState(0);
  const navigate = useNavigate();
  const [alertList, setAlertList] = useState([]);

  const fetchBadgeCount = useCallback(async () => {
    if (!user?.id) return [];

    try {
      const alertFlags = await getBidAlertShowFlag();
      // Server already filters to this user's unseen flags.
      const alertFlagsFilterData = alertFlags?.data || [];
      setAlertNum(alertFlagsFilterData.length);
      return alertFlagsFilterData;
    } catch (error) {
      console.error("Failed to load bid alert badge:", error);
      return [];
    }
  }, [user?.id]);

  const fetchAlertList = useCallback(async () => {
    if (!user?.id) return;

    try {
      const alertFlagsFilterData = await fetchBadgeCount();
      if (!alertFlagsFilterData.length) {
        setAlertList([]);
        return;
      }

      const alertShowData = await getBidShowAlertList(alertFlagsFilterData);
      setAlertList(alertShowData?.data || []);
    } catch (error) {
      console.error("Failed to load bid alerts:", error);
    }
  }, [user?.id, fetchBadgeCount]);

  useEffect(() => {
    if (!user?.id) return undefined;

    // Badge only on first paint; defer so it does not block shell render.
    let idleId;
    let timeoutId;
    const run = () => fetchBadgeCount();
    if (typeof window !== "undefined" && window.requestIdleCallback) {
      idleId = window.requestIdleCallback(run, { timeout: 2500 });
    } else {
      timeoutId = setTimeout(run, 400);
    }
    return () => {
      if (idleId != null && window.cancelIdleCallback) {
        window.cancelIdleCallback(idleId);
      }
      if (timeoutId != null) clearTimeout(timeoutId);
    };
  }, [user?.id, fetchBadgeCount]);

  useEffect(() => {
    if (!socket) return undefined;

    const onNewAlert = () => {
      fetchBadgeCount();
    };

    socket.on("receive_new_bid_alert", onNewAlert);
    return () => {
      socket.off("receive_new_bid_alert", onNewAlert);
    };
  }, [socket, fetchBadgeCount]);

  const handleNotificationOpen = (event) => {
    setAnchorEl(event.currentTarget);
    setAlertNum(0);
    fetchAlertList();
    if (user?.id) {
      unSetBadgeNums(user.id);
    }
  };

  const handleNotificationClose = () => {
    setAnchorEl(null);
  };

  const handleViewAllBids = () => {
    handleNotificationClose();
    navigate("/bids");
  };

  const isNotificationOpen = Boolean(anchorEl);
  const visibleAlerts = Array.isArray(alertList) ? alertList : [];

  return (
    <>
      <IconButton
        size="medium"
        aria-label={
          alertNum > 0
            ? `Show ${alertNum} new notifications`
            : "Show notifications"
        }
        onClick={handleNotificationOpen}
        sx={{
          color: "#475569",
          "&:hover": { backgroundColor: "#E8F3FF", color: "#147EFF" },
        }}
      >
        <Badge
          badgeContent={alertNum}
          color="error"
          max={99}
          sx={{
            "& .MuiBadge-badge": {
              fontSize: "0.65rem",
              minWidth: 18,
              height: 18,
            },
          }}
        >
          <NotificationsIcon fontSize="small" />
        </Badge>
      </IconButton>

      <Popover
        open={isNotificationOpen}
        anchorEl={anchorEl}
        onClose={handleNotificationClose}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
        transformOrigin={{ vertical: "top", horizontal: "right" }}
        slotProps={{
          paper: {
            className:
              "mt-2 w-[22rem] overflow-hidden rounded-xl border border-slate-200 shadow-lg",
            elevation: 0,
          },
        }}
      >
        <div className="flex items-center justify-between border-b border-slate-100 bg-slate-50 px-4 py-3">
          <div>
            <p className="text-sm font-semibold text-slate-900">Notifications</p>
            <p className="text-xs text-slate-500">
              {visibleAlerts.length > 0
                ? `${visibleAlerts.length} recent bid alert${
                    visibleAlerts.length === 1 ? "" : "s"
                  }`
                : "You're all caught up"}
            </p>
          </div>
        </div>

        <div className="max-h-80 overflow-y-auto">
          {visibleAlerts.length > 0 ? (
            <ul className="divide-y divide-slate-100">
              {visibleAlerts.map((alert, index) => {
                const route = getAlertRoute(alert);
                const key = alert?.id ?? alert?.alert_id ?? `alert-${index}`;

                return (
                  <li key={key} className="px-4 py-3 hover:bg-slate-50/80">
                    <div className="mb-1.5 flex items-center gap-2">
                      <span className="inline-flex size-2 shrink-0 rounded-full bg-primary" />
                      <p className="text-sm font-medium text-slate-900">
                        {alert?.content?.platformStatus
                          ? `${route.platform || "Board"} ${String(
                              alert.content.platformStatus
                            ).toLowerCase()}`
                          : "New bid"}
                        {route.amount ? (
                          <span className="text-primary"> · {route.amount}</span>
                        ) : null}
                      </p>
                      {route.platform ? (
                        <span className="ml-auto rounded-full bg-primary-100 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-primary-800">
                          {route.platform}
                        </span>
                      ) : null}
                    </div>
                    <div className="pl-4 text-xs leading-relaxed text-slate-600">
                      <p>
                        <span className="font-medium text-slate-500">From</span>{" "}
                        {route.origin}
                      </p>
                      <p>
                        <span className="font-medium text-slate-500">To</span>{" "}
                        {route.destination}
                      </p>
                    </div>
                  </li>
                );
              })}
            </ul>
          ) : (
            <div className="px-4 py-10 text-center">
              <div className="mx-auto mb-3 flex size-10 items-center justify-center rounded-full bg-slate-100 text-slate-400">
                <NotificationsIcon fontSize="small" />
              </div>
              <p className="text-sm font-medium text-slate-800">No new alerts</p>
              <p className="mt-1 text-xs text-slate-500">
                Bid updates will show up here.
              </p>
            </div>
          )}
        </div>

        <div className="border-t border-slate-100 bg-white p-2">
          <button
            type="button"
            onClick={handleViewAllBids}
            className="w-full rounded-lg px-3 py-2.5 text-sm font-semibold text-primary transition-colors hover:bg-primary-100"
          >
            View all bids
          </button>
        </div>
      </Popover>
    </>
  );
};

export default PopupAlert;
